/*
  <hover-buttons> — shared button hover.

  Default mode — cursor blob (the hero-button effect, same as <social-blobs>):
    enter → a circular fill GROWS from the exact point the cursor crossed the
            edge until it covers the button (clipped by overflow:hidden)
    leave → it SHRINKS back toward the exit point
  Text colour inverts via CSS. Built on the Web Animations API (no deps).
  The fill's circle geometry lives on .hover-button__fill in base.css; each
  variant (--primary/--light/--accent) only sets its colour.

  Ink mode — <hover-buttons data-fill="ink"> (needs GSAP):
    enter → a black goo blob GROWS from the cursor's entry point until it fills
            the button (clipped by overflow:hidden), as if the tear cursor poured
            its ink in. Fires `ink:absorb` so the cursor trail hides itself.
    leave → the blob DRAINS toward the cursor's exit point and shrinks away.
            Fires `ink:release` so the cursor trail comes back.
  The blob is centred on its own (left, top) via a CSS translate(-50%,-50%); the
  JS animates left/top (position) and width/height (size) — never `transform` —
  so the goo edge keeps a constant, organic thickness instead of blurring as it
  scales. See .hover-button--ink in base.css.

  Skipped on touch / no-hover pointers and when the section opts out
  (data-animate="false"). Blob mode animates regardless of reduced motion (a
  brief, gentle decoration); ink mode is hover-only too.

  Wrap one or more button-like elements; each needs a `[data-fill]` blob child.
*/
(function () {
  if (customElements.get('hover-buttons')) return;

  // Grow matches grensena.lt's button: 0.3s, ease-in-out (power2.inOut ≈ the CSS
  // cubic-bezier(0.42, 0, 0.58, 1) they use).
  const BLOB_DURATION = 0.3;
  const BLOB_EASE = 'power2.inOut';
  // ...but the un-hover drain scales with how far the cursor is from the button
  // when you leave: close → relaxed, far (a fast flick) → quick, so it keeps up.
  const DRAIN_NEAR = 0.45;
  const DRAIN_FAR = 0.1;
  const DRAIN_EASE = 'power2.in';
  const PAD = 1.15;       // overshoot so the circle clears the far corner cleanly

  // Latest pointer position, used to measure the leave distance for the drain.
  let ptrX = 0, ptrY = 0;
  window.addEventListener('pointermove', (e) => { ptrX = e.clientX; ptrY = e.clientY; }, { passive: true });

  function drainDuration(r) {
    const dist = Math.hypot(ptrX - (r.left + r.width / 2), ptrY - (r.top + r.height / 2));
    const k = Math.min(1, dist / 500); // ~500px away counts as "far"
    return DRAIN_NEAR + (DRAIN_FAR - DRAIN_NEAR) * k;
  }

  // The goo filter that softens the spreading ink edge. Injected once, into a
  // zero-size (NOT display:none) carrier so Firefox still resolves url(#…).
  function ensureInkFilter() {
    if (document.getElementById('ink-fill-goo')) return;
    const carrier = document.createElement('div');
    carrier.className = 'hidemouse';
    carrier.innerHTML =
      '<svg><defs><filter id="ink-fill-goo">' +
      '<feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur"/>' +
      '<feColorMatrix in="blur" mode="matrix" ' +
      'values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 24 -9" result="goo"/>' +
      '</filter></defs></svg>';
    document.body.appendChild(carrier);
  }

  // Diameter needed for a circle centred at (px, py) to cover the whole button.
  function coverDiameter(r, px, py) {
    const far = Math.max(
      Math.hypot(px, py),
      Math.hypot(r.width - px, py),
      Math.hypot(px, r.height - py),
      Math.hypot(r.width - px, r.height - py)
    );
    return far * 2 * PAD;
  }

  function inkGrow(gsap, button, fill, e) {
    const r = button.getBoundingClientRect();
    const px = e.clientX - r.left;
    const py = e.clientY - r.top;
    const d = coverDiameter(r, px, py);
    gsap.killTweensOf(fill);
    gsap.set(fill, { left: px, top: py });
    // Once the fill has fully covered the button, reveal the purple border.
    gsap.to(fill, {
      width: d, height: d, duration: BLOB_DURATION, ease: BLOB_EASE,
      onComplete: () => button.classList.add('is-ink-filled'),
    });
    // Suck the tear toward the button's CENTRE (deep inside), not the entry edge —
    // by the time you hover, the trailing dots have caught up and clustered on the
    // cursor, so a centre target gives them a real distance to travel inward.
    document.dispatchEvent(new CustomEvent('ink:absorb', {
      detail: { x: r.left + r.width / 2, y: r.top + r.height / 2 }
    }));
  }

  function inkDrain(gsap, button, fill, e) {
    const r = button.getBoundingClientRect();
    // Drop the purple border the instant the out animation begins.
    button.classList.remove('is-ink-filled');
    gsap.killTweensOf(fill);
    // Blob drains at the regular hover-button speed; always restarts so the
    // blob-out animation plays even on a fast exit.
    gsap.to(fill, {
      left: e.clientX - r.left,
      top: e.clientY - r.top,
      width: 0,
      height: 0,
      duration: drainDuration(r),
      ease: DRAIN_EASE,
    });
    // Hand the button centre to the cursor so its ink can spit back out from the
    // button toward wherever the cursor now is (faster the farther it has moved).
    document.dispatchEvent(new CustomEvent('ink:release', {
      detail: { cx: r.left + r.width / 2, cy: r.top + r.height / 2 }
    }));
  }

  customElements.define('hover-buttons', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.animate === 'false') return;
      if (!window.matchMedia('(hover: hover)').matches) return;
      // The menu drawer re-parents itself to <body> when it opens, which
      // reconnects everything inside it. Binding twice would run two blob
      // animations per hover, each cancelling the other.
      if (this._bound) return;
      this._bound = true;

      this.classList.add('hover-buttons--ready');

      if (this.dataset.fill === 'ink') {
        this.initInk();
        return;
      }

      this.querySelectorAll('[data-fill]').forEach((fill) => {
        const button = fill.closest('.hover-button') || fill.parentElement;
        if (!button) return;
        button.addEventListener('mouseenter', (e) => this.blob(button, fill, e, true));
        button.addEventListener('mouseleave', (e) => this.blob(button, fill, e, false));
      });
    }

    initInk() {
      const gsap = window.gsap;
      if (!gsap) return; // ink mode is GSAP-driven; without it the pill just sits static
      ensureInkFilter();

      this.querySelectorAll('[data-fill]').forEach((fill) => {
        const button = fill.closest('.hover-button') || fill.parentElement;
        if (!button) return;
        button.addEventListener('pointerenter', (e) => inkGrow(gsap, button, fill, e));
        button.addEventListener('pointerleave', (e) => inkDrain(gsap, button, fill, e));
      });
    }

    // Cursor-anchored circle that GROWS via width/height, NOT transform:
    // an animated transform puts the fill on its own compositor layer, and
    // the rounded overflow clip cuts that layer slightly differently than
    // the button's painted background — leaving a ~1px ring of the colour
    // beneath at the curved edge. Animating the SIZE keeps the circle
    // painted in the button's own layer, so the clip cuts fill and
    // background identically and the pill reads fully covered — same
    // reasoning as ink mode above ("never `transform`").
    blob(button, fill, event, entering) {
      const r = button.getBoundingClientRect();
      const px = event.clientX - r.left;
      const py = event.clientY - r.top;
      fill.style.left = `${px}px`;
      fill.style.top = `${py}px`;

      // Single-keyframe animations start from the CURRENT computed size
      // (preserved via commitStyles), so interrupted hovers continue smoothly.
      if (fill._anim) {
        try { fill._anim.commitStyles(); } catch (e) { /* no-op */ }
        fill._anim.cancel();
      }
      const duration = parseInt(this.dataset.duration, 10) || 450;
      // In: smooth start that accelerates to a fast finish. Out: plain ease.
      const easing = entering ? 'cubic-bezier(0.5, 0, 1, 1)' : 'ease';
      const d = entering ? Math.ceil(coverDiameter(r, px, py)) : 0;
      fill._anim = fill.animate(
        [{ width: `${d}px`, height: `${d}px` }],
        { duration, easing, fill: 'forwards' }
      );
    }
  });
})();
